import AddIcon from "@mui/icons-material/Add";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { isProtectedClearanceId } from "../clearanceHelpers";
import { GroupsTruncatedAlert } from "@/components/GroupsTruncatedAlert";
import { LoadingRow } from "@/components/LoadingRow";

interface ClearanceListProps {
  clearances: { id: string; name?: string }[];
  selectedId: string | null;
  search: string;
  onSearchChange: (value: string) => void;
  onSelect: (id: string) => void;
  onNew: () => void;
  isLoading: boolean;
  hasLoadError: boolean;
  showFallbackWarning: boolean;
}

export function ClearanceList({
  clearances,
  selectedId,
  search,
  onSearchChange,
  onSelect,
  onNew,
  isLoading,
  hasLoadError,
  showFallbackWarning,
}: ClearanceListProps) {
  const { t } = useTranslation();

  return (
    <Paper
      variant="outlined"
      sx={{
        width: { xs: "100%", lg: "30%" },
        minWidth: { xs: 0, lg: 320 },
        boxSizing: "border-box",
        p: 1.5,
        overflow: "auto",
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
          <TextField
            label={t("clearances.search")}
            value={search}
            onChange={event => onSearchChange(event.target.value)}
            size="small"
            sx={{ flex: "1 1 160px", minWidth: 0 }}
          />
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={onNew}
            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          >
            {t("clearances.new")}
          </Button>
        </Stack>

        <Divider />

        {isLoading ? (
          <LoadingRow label={t("clearances.loading")} />
        ) : hasLoadError ? (
          <Alert severity="error">{t("clearances.loadError")}</Alert>
        ) : (
          <List dense disablePadding>
            {clearances.map(group => (
              <ListItemButton
                key={group.id}
                selected={selectedId === group.id}
                onClick={() => onSelect(group.id)}
              >
                <ListItemText
                  primary={group.name ?? group.id}
                  slotProps={{ secondary: { component: "div" } }}
                  secondary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {group.id}
                      </Typography>
                      {isProtectedClearanceId(group.id) ? (
                        <Chip size="small" color="default" label={t("clearances.system")} />
                      ) : null}
                    </Stack>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}

        {showFallbackWarning ? (
          <Alert severity="warning">{t("clearances.securityBranchWarning")}</Alert>
        ) : null}
        <GroupsTruncatedAlert />
      </Stack>
    </Paper>
  );
}
